/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import PlaybookDetailQuery$parameters from '@jira/playbooks__edit-screen/src/__generated__/PlaybookDetailQuery$parameters.ts';
import type { Queries, ExtraProps } from '@jira/playbooks__edit-screen/src/FormValues.types.tsx';
import type { PlaybookEditViewEntryPointEntrypointProps } from './PlaybookEditViewEntryPoint.entrypoint.types.tsx';

type PlaybookEditViewEntryPointComponent = EntryPointComponent<
	Queries | Record<string, never>,
	Record<string, never>,
	PlaybookEditViewEntryPointEntrypointProps,
	ExtraProps
>;
/**
 * NOTE - Please update src/packages/playbooks/edit-screen/src/entrypoint.tsx
 * as that is used in integration test and ideally both should be same except queries
 */

const playbookEditViewEntryPoint: EntryPoint<
	PlaybookEditViewEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForInteraction<PlaybookEditViewEntryPointComponent>(
		/* webpackChunkName: "async-spa-apps-playbooks-edit" */ './index.tsx',
	),
	getPreloadProps: ({ context, tenantContext }: EntryPointRouteParams) => {
		const { projectKey, playbookId = '' } = context.match.params;
		const { fromPlaybookId = '' } = context.query;
		const aiResultId = fg('phantom-4816-ai-playbook-creation-fe')
			? context.query.aiResultId
			: undefined;
		if (!projectKey) {
			throw new Error('projectKey is required');
		}
		const { cloudId, activationId } = tenantContext;
		const isNewPlaybook = !!playbookId?.includes(NEW_PLAYBOOK_IDENTIFIER);
		const callablePlaybookId = getPlaybookId(playbookId, fromPlaybookId);
		const playbookAri = toPlaybookAri({
			siteId: cloudId,
			activationId,
			playbookId: callablePlaybookId,
		});
		const isNewPlaybookCheck = isNewPlaybook && !fromPlaybookId;
		if (isNewPlaybookCheck) {
			// This is complete new playbook creation
			return {
				extraProps: {
					playbookAri,
					isNew: true,
					variant: 'new' as const,
					aiResultId,
				},
			};
		}
		return {
			queries: {
				PlaybookDetailQuery: {
					parameters: PlaybookDetailQuery$parameters,
					variables: {
						playbookAri,
						cloudId,
						isMediaSupportEnabled: fg('playbooks_step_image_file_support'),
					},
				},
			},
			extraProps: {
				playbookAri,
				isNew: false,
				variant: isNewPlaybook && !!fromPlaybookId ? ('copy' as const) : ('edit' as const),
			},
		};
	},
});

export default playbookEditViewEntryPoint;

function toPlaybookAri({
	siteId,
	activationId,
	playbookId,
}: {
	siteId: string;
	activationId: string;
	playbookId: string;
}) {
	return `ari:cloud:jira:${siteId}:playbook/activation/${activationId}/${playbookId}`;
}

const getPlaybookId = (playbookId: string | null, fromPlaybookId: string): string => {
	if (fromPlaybookId) {
		return fromPlaybookId;
	}
	return playbookId || '';
};

const NEW_PLAYBOOK_IDENTIFIER = '_new_';
